import React, { useEffect, useState, useRef } from "react";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import soldSound from "../../../../assets/audios/sold.mp3"; // Make sure this path is correct
import bidUnderApprovalSound from "../../../../assets/audios/bid-under-approval.wav";

const CircularProgress = ({
  activeBid,
  bonusTime,
  setBonusTime,
  resetTimer,
  currentBid,
  pusherStatus,
  timeLeft,
  isBonusTime
}) => {
  const [displayText, setDisplayText] = useState("Getting Started");
  const [key, setKey] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [showPusherStatus, setShowPusherStatus] = useState(false);
  const [currentPusherStatus, setCurrentPusherStatus] = useState("");
  const [timerDuration, setTimerDuration] = useState(10);
  const soldAudio = useRef(new Audio(soldSound));
  const bidUnderApprovalAudio = useRef(new Audio(bidUnderApprovalSound));
  const isPusherBonusTime = useRef(false);
  const timerCompleted = useRef(false);
  const isInitialLoad = useRef(true);
  const TIMER_SIZE = 150; // keep circle size consistent with responsive inner text
  
  // This effect handles the initial page load/reload using backend time
  useEffect(() => {
    if (timeLeft !== undefined) {
      // Show "Getting Started" when timeLeft is -1, regardless of other states
      if (timeLeft === -1) {
        setDisplayText("Getting Started");
        setIsTimerActive(false);
        setShowPusherStatus(false);
        setTimerDuration(10);
        return;
      }
      
      setTimerDuration(timeLeft > 0 ? timeLeft : 10);
      
      if (isBonusTime) {
        setBonusTime(true);
        setDisplayText("BONUS TIME");
      } else if (activeBid) {
        setBonusTime(false);
        setDisplayText(`$${activeBid}`);
      }
      
      // Only start timer on initial load if we have a valid timeLeft
      if (isInitialLoad.current && timeLeft > 0) {
        setIsTimerActive(true);
        setKey(prev => prev + 1);
        isInitialLoad.current = false;
      }
      
      setShowPusherStatus(false);
    }
  }, [timeLeft, isBonusTime, activeBid, setBonusTime]);

  // Reset states when activeBid changes (new car)
  useEffect(() => {
    setShowPusherStatus(false);
    setCurrentPusherStatus("");
    timerCompleted.current = false;
    isPusherBonusTime.current = false;
    isInitialLoad.current = true;
  }, [activeBid]);

  // Handle bonus time starts
  useEffect(() => {
    // Don't process bonus time if timeLeft is -1
    if (timeLeft === -1) return;

    if (bonusTime && !timerCompleted.current) {
      if (!isBonusTime || isPusherBonusTime.current) {
        setTimerDuration(10);
        isPusherBonusTime.current = true;
      }
      
      setDisplayText("BONUS TIME");
      // Only start timer if it's from a Pusher event
      if (isPusherBonusTime.current) {
        setIsTimerActive(true);
        setKey(prev => prev + 1);
      }
      setShowPusherStatus(false);
    } else if (activeBid && !showPusherStatus && !timerCompleted.current) {
      setDisplayText(`$${activeBid}`);
      // Don't automatically start timer for new bids
      setShowPusherStatus(false);
    }
  }, [bonusTime, activeBid, currentBid, showPusherStatus, isBonusTime, timeLeft]);

  // Handle timer reset (from Pusher events)
  useEffect(() => {
    if (resetTimer && (bonusTime || (activeBid && !showPusherStatus)) && !timerCompleted.current) {
      setIsTimerActive(true);
      setKey(prev => prev + 1);
      setShowPusherStatus(false);
    }
  }, [resetTimer, bonusTime, activeBid, showPusherStatus]);

  // Handle when pusher status changes
  useEffect(() => {
    // Don't process pusher status if timeLeft is -1
    if (timeLeft === -1) return;

    if (pusherStatus) {
      setCurrentPusherStatus(pusherStatus);
      setDisplayText(pusherStatus);
      setIsTimerActive(false); // Stop the timer immediately
      setShowPusherStatus(true);

      setBonusTime(false); //new line
      
      const lower = (pusherStatus || "").toLowerCase();
      const isSoldOnApproval = lower.includes("sold on approval") || lower.includes("approval");

      const playStatusSound = async () => {
        try {
          if (isSoldOnApproval) {
            bidUnderApprovalAudio.current.currentTime = 0;
            await bidUnderApprovalAudio.current.play();
          } else {
            soldAudio.current.currentTime = 0;
            await soldAudio.current.play();
          }
        } catch (error) {}
      };

      playStatusSound();
    }
  }, [pusherStatus, timeLeft, setBonusTime]);

  // Cleanup audio on component unmount
  useEffect(() => {
    const audio = soldAudio.current;
    const approvalAudio = bidUnderApprovalAudio.current;
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      if (approvalAudio) {
        approvalAudio.pause();
        approvalAudio.currentTime = 0;
      }
    };
  }, []);

  const handleComplete = () => {
    if (bonusTime) {
      timerCompleted.current = true;
      setBonusTime(false);
      setShowPusherStatus(true);
      setDisplayText(pusherStatus || ""); 
      setIsTimerActive(false);
    } else {
      // Don't automatically start bonus time timer
      setIsTimerActive(false);
      setBonusTime(true);
      setTimerDuration(10);
      isPusherBonusTime.current = true;
    }
    return { shouldRepeat: false };
  };

  // Calculate color time points based on current duration
  const getColorTimePoints = () => {
    return [
      timerDuration * 0.7, // 70% of time remaining - green
      timerDuration * 0.5, // 50% of time remaining - yellow
      timerDuration * 0.3, // 30% of time remaining - orange
      0                    // 0% of time remaining - red
    ];
  };

  return (
    <div className="relative flex justify-center items-center lg:w-full lg:h-full">
      <CountdownCircleTimer
        key={key}
        isPlaying={isTimerActive}
        duration={timerDuration}
        size={TIMER_SIZE}
        colors={["#10B981", "#F59E0B", "#F97316", "#EF4444"]}
        colorsTime={getColorTimePoints()}
        onComplete={handleComplete}
      >
        {() => (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className={`font-bold text-center leading-tight break-words whitespace-normal px-2 ${
                showPusherStatus ? "text-gray-600" : 
                bonusTime ? "text-orange-600" : "text-green-600"
              } text-[clamp(12px,3.5vw,22px)]`}
              style={{ maxWidth: `${TIMER_SIZE - 24}px` }}
            >
              {showPusherStatus ? currentPusherStatus : displayText}
            </div>
          </div>
        )}
      </CountdownCircleTimer>
    </div>
  );
};

export default CircularProgress;